import { ErrorHandler, Injectable, inject } from '@angular/core';
import { ApplicationInsightsService } from './application-insights.service';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly applicationInsights = inject(ApplicationInsightsService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  handleError(error: Error): void {
    // Always log to console for development
    console.error('Global error caught:', error);

    // Extract useful context information
    const errorContext = this.extractErrorContext(error);

    // Get user context if available
    const userContext = this.extractUserContext();

    // Send to Application Insights
    this.applicationInsights.trackException(error, {
      severity: 'critical',
      errorType: 'unhandled',
      currentRoute: this.router.url,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...errorContext,
      ...userContext,
    });

    // Don't rethrow the error to prevent app crashes
    // Angular's default behavior would be to rethrow it
  }

  private extractUserContext(): Record<string, string> {
    const userContext: Record<string, string> = {};

    try {
      // Get current user synchronously (if available)
      let currentUser: any = null;
      this.authService.currentUser
        .pipe()
        .subscribe(user => {
          currentUser = user;
        })
        .unsubscribe();

      if (currentUser) {
        // Include user email as primary identifier
        if (currentUser.email) {
          userContext['userEmail'] = currentUser.email;
          userContext['userId'] = currentUser.email; // For consistency with server-side
        }

        // Include user name
        if (currentUser.firstName || currentUser.lastName) {
          const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
          if (fullName) userContext['userName'] = fullName;
        }

        // Include database user ID
        if (currentUser.id) {
          userContext['databaseUserId'] = currentUser.id;
        }

        // Include client context
        if (currentUser.clientId) {
          userContext['clientId'] = currentUser.clientId.toString();
        }

        // Include admin status
        if (currentUser.isAdmin !== undefined) {
          userContext['isAdmin'] = currentUser.isAdmin.toString();
        }

        // Include role information
        if (currentUser.globalRole) {
          userContext['globalRole'] = currentUser.globalRole;
        }

        userContext['userContextAvailable'] = 'true';
      } else {
        userContext['userContextAvailable'] = 'false';
      }
    } catch (userError) {
      userContext['userContextError'] = 'failed-to-extract';
      console.warn('Failed to extract user context for error:', userError);
    }

    return userContext;
  }

  private extractErrorContext(error: Error): Record<string, string> {
    const context: Record<string, string> = {};

    // Extract error name and message
    context['errorName'] = error.name || 'Unknown';
    context['errorMessage'] = error.message || 'No message provided';

    // Extract stack trace (first few lines for context)
    if (error.stack) {
      context['stackTrace'] = error.stack;
      // Extract just the first few lines for summary
      const stackLines = error.stack.split('\n').slice(0, 5);
      context['stackSummary'] = stackLines.join(' | ');
    }

    // Try to extract component information from stack trace
    const componentMatch = error.stack?.match(/at\s+(\w+Component)\./);
    if (componentMatch) {
      context['component'] = componentMatch[1];
    }

    // Check for specific Angular errors
    if (error.message?.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
      context['errorCategory'] = 'angular-change-detection';
    } else if (error.message?.includes('Cannot read prop')) {
      context['errorCategory'] = 'null-reference';
    } else if (error.message?.includes('undefined')) {
      context['errorCategory'] = 'undefined-access';
    } else if (error.name === 'ChunkLoadError') {
      context['errorCategory'] = 'chunk-load-failure';
    } else if (error.name === 'TypeError') {
      context['errorCategory'] = 'type-error';
    } else if (error.name === 'ReferenceError') {
      context['errorCategory'] = 'reference-error';
    } else {
      context['errorCategory'] = 'general';
    }

    // Extract URL information
    context['currentUrl'] = window.location.href;
    context['origin'] = window.location.origin;
    context['pathname'] = window.location.pathname;

    // Browser information
    context['browserLanguage'] = navigator.language;
    context['platform'] = navigator.platform;
    context['cookieEnabled'] = navigator.cookieEnabled.toString();

    // Viewport information
    context['viewportWidth'] = window.innerWidth.toString();
    context['viewportHeight'] = window.innerHeight.toString();
    context['screenWidth'] = screen.width.toString();
    context['screenHeight'] = screen.height.toString();

    return context;
  }
}
